import { useState } from "react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { CalendarIcon, MessageCircle } from "lucide-react";
import { toast } from "sonner";

import { FadeUp } from "@/components/motion/Reveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICES } from "@/data/site";
import { openWhatsApp } from "@/lib/wa";

const TIME_RANGES = [
  "Sabah (08.00 – 12.00)",
  "Öğle (12.00 – 15.00)",
  "Öğleden sonra (15.00 – 19.00)",
  "Fark etmez",
];

export default function AppointmentForm() {
  const [form, setForm] = useState({ name: "", phone: "", service: "", time: "", note: "" });
  const [date, setDate] = useState(null);
  const [kvkk, setKvkk] = useState(false);
  const [open, setOpen] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Lütfen ad soyad ve telefon bilgilerinizi girin.");
      return;
    }
    if (!kvkk) {
      toast.error("Devam etmek için KVKK aydınlatma metnini onaylayın.");
      return;
    }
    openWhatsApp(
      `Merhaba Hülya Suna Beauty, randevu talebinde bulunmak istiyorum.\n\n` +
        `Ad Soyad: ${form.name}\n` +
        `Telefon: ${form.phone}\n` +
        `İlgilendiğim Hizmet: ${form.service || "-"}\n` +
        `Tercih Edilen Gün: ${date ? format(date, "d MMMM yyyy", { locale: tr }) : "-"}\n` +
        `Tercih Edilen Saat Aralığı: ${form.time || "-"}\n` +
        `Not: ${form.note || "-"}`
    );
    toast.success("Talebiniz WhatsApp üzerinden hazırlandı. Salon onayından sonra kesinleşir.");
  };

  return (
    <section id="randevu" className="bg-espresso text-ivory py-20 sm:py-28 grain relative" data-testid="appointment-section">
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <FadeUp>
            <p className="eyebrow text-gold mb-4">Randevu</p>
          </FadeUp>
          <FadeUp delay={0.05}>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.03] mb-6">
              Randevu <span className="italic text-gold">Talebi</span> Oluşturun
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="text-ivory/70 leading-relaxed max-w-md">
              Randevu talebiniz WhatsApp üzerinden iletilir. Salon onayından sonra
              randevunuz kesinleşir. Sağlık bilgisi paylaşmanıza gerek yoktur.
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.1} className="lg:col-span-7">
          <form
            onSubmit={submit}
            className="bg-ivory/[0.04] border border-ivory/10 rounded-2xl p-6 sm:p-8 grid sm:grid-cols-2 gap-5"
            data-testid="appointment-form"
          >
            <div className="sm:col-span-1">
              <Label htmlFor="ad" className="text-ivory/80 mb-2 block">Ad Soyad</Label>
              <Input
                id="ad"
                value={form.name}
                onChange={(e) => set("name")(e.target.value)}
                placeholder="Adınız Soyadınız"
                data-testid="appointment-name"
                className="bg-transparent border-ivory/20 text-ivory placeholder:text-ivory/40 focus-visible:ring-gold"
              />
            </div>

            <div className="sm:col-span-1">
              <Label htmlFor="tel" className="text-ivory/80 mb-2 block">Telefon</Label>
              <Input
                id="tel"
                type="tel"
                value={form.phone}
                onChange={(e) => set("phone")(e.target.value)}
                placeholder="05xx xxx xx xx"
                data-testid="appointment-phone"
                className="bg-transparent border-ivory/20 text-ivory placeholder:text-ivory/40 focus-visible:ring-gold"
              />
            </div>

            <div className="sm:col-span-2">
              <Label className="text-ivory/80 mb-2 block">İlgilendiğiniz Hizmet</Label>
              <Select value={form.service} onValueChange={set("service")}>
                <SelectTrigger
                  data-testid="appointment-service"
                  className="bg-transparent border-ivory/20 text-ivory focus:ring-gold data-[placeholder]:text-ivory/40"
                >
                  <SelectValue placeholder="Bir hizmet seçin" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICES.map((s) => (
                    <SelectItem key={s.slug} value={s.title}>{s.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-1">
              <Label className="text-ivory/80 mb-2 block">Tercih Edilen Gün</Label>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="appointment-day"
                    className="flex w-full items-center justify-between rounded-md border border-ivory/20 bg-transparent px-3 py-2 text-sm text-left h-10"
                  >
                    <span className={date ? "text-ivory" : "text-ivory/40"}>
                      {date ? format(date, "d MMMM yyyy", { locale: tr }) : "Gün seçin"}
                    </span>
                    <CalendarIcon size={16} className="text-gold" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => {
                      setDate(d);
                      setOpen(false);
                    }}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    locale={tr}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="sm:col-span-1">
              <Label className="text-ivory/80 mb-2 block">Tercih Edilen Saat Aralığı</Label>
              <Select value={form.time} onValueChange={set("time")}>
                <SelectTrigger
                  data-testid="appointment-time"
                  className="bg-transparent border-ivory/20 text-ivory focus:ring-gold data-[placeholder]:text-ivory/40"
                >
                  <SelectValue placeholder="Saat aralığı seçin" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_RANGES.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="not" className="text-ivory/80 mb-2 block">Notunuz</Label>
              <Textarea
                id="not"
                value={form.note}
                onChange={(e) => set("note")(e.target.value)}
                placeholder="Eklemek istedikleriniz (isteğe bağlı)"
                rows={3}
                data-testid="appointment-note"
                className="bg-transparent border-ivory/20 text-ivory placeholder:text-ivory/40 focus-visible:ring-gold resize-none"
              />
            </div>

            <div className="sm:col-span-2 flex items-start gap-3">
              <Checkbox
                id="kvkk"
                checked={kvkk}
                onCheckedChange={(v) => setKvkk(!!v)}
                data-testid="appointment-kvkk"
                className="mt-0.5 border-ivory/40 data-[state=checked]:bg-gold data-[state=checked]:border-gold"
              />
              <Label htmlFor="kvkk" className="text-ivory/70 text-sm font-normal leading-relaxed cursor-pointer">
                Kişisel verilerimin randevu talebimin iletilmesi amacıyla işlenmesini,
                KVKK Aydınlatma Metni kapsamında kabul ediyorum.
              </Label>
            </div>

            <div className="sm:col-span-2">
              <button
                type="submit"
                data-testid="appointment-submit-button"
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-espresso text-sm font-semibold transition-transform duration-300 hover:scale-[1.02] active:scale-95"
              >
                <MessageCircle size={18} />
                WhatsApp’tan Talep Gönder
              </button>
              <p className="mt-3 text-center text-xs text-ivory/50">
                Randevu talebiniz WhatsApp üzerinden iletilir. Salon onayından sonra
                randevunuz kesinleşir.
              </p>
            </div>
          </form>
        </FadeUp>
      </div>
    </section>
  );
}
